import { useCallback, useMemo } from 'react';
import { AdviseCurrentSuppliers, AdviseEngagement, AdviseSupplierContract } from '../../../types/advise';
import { updateAdviseCurrentSupplier, updateAdviseInvoiceLineItems } from '../../../Api/Advise/Engagements';
import { CallResult } from '../../../Api/callExternalApi';
import {
  lineItemsReplacement,
  withContractChange,
  withInvoiceLineChange,
  withSupplierChange
} from '../../Compounds/Advise/SideBySideView/InvoiceView.calc';
import { CHANGE_FAILED, changeRefusal, findSupplier, NO_SUPPLIERS } from './currentSupplierRereads';
import { CurrentSuppliersQueue } from './useCurrentSuppliersQueue';

// What a save that has nothing left to send resolves to, so it never sends a change built from the wrong copy.
const NOT_SENT: CallResult<AdviseEngagement> = { data: null, error: { message: CHANGE_FAILED } };

type Change = (current: AdviseCurrentSuppliers) => AdviseCurrentSuppliers;
type Send = (id: string, changed: AdviseCurrentSuppliers) => Promise<CallResult<AdviseEngagement>>;

/** The Invoice View's edits: each shows at once, then saves in turn, building what it sends from the last save. */
export const useInvoiceViewSaves = ({
  engagementRef,
  saved,
  canEdit,
  show,
  showRefusal,
  sendThenShow
}: CurrentSuppliersQueue) => {
  const showThenSend = useCallback(
    async (change: Change, send: Send) => {
      if (!canEdit || !engagementRef.current?.id) return;
      show(change(engagementRef.current.current_suppliers ?? NO_SUPPLIERS));
      // Built when it is sent, so it carries the line-items version the change queued ahead of it saved.
      // An engagement opened since this was queued has replaced the saved copy, so it is dropped rather than sent.
      const sent = await sendThenShow((id) =>
        engagementRef.current?.id === id ? send(id, change(saved.current)) : Promise.resolve(NOT_SENT)
      );
      if (sent?.error) showRefusal(changeRefusal(sent));
    },
    [canEdit, engagementRef, saved, sendThenShow, show, showRefusal]
  );

  const changeLine = useCallback(
    (supplierId: string, invoiceId: string, lineId: string, change: { in_scope?: boolean; category?: string }) =>
      showThenSend(
        (current) => withInvoiceLineChange(current, supplierId, invoiceId, lineId, change),
        (id, changed) => {
          const invoice = findSupplier(changed, supplierId)?.invoices.find(({ id: each }) => each === invoiceId);
          if (!invoice) return Promise.resolve(NOT_SENT);
          return updateAdviseInvoiceLineItems(id, supplierId, invoiceId, lineItemsReplacement(invoice));
        }
      ),
    [showThenSend]
  );

  const setLineInScope = useCallback(
    (supplierId: string, invoiceId: string, lineId: string, inScope: boolean) =>
      changeLine(supplierId, invoiceId, lineId, { in_scope: inScope }),
    [changeLine]
  );

  const setLineCategory = useCallback(
    (supplierId: string, invoiceId: string, lineId: string, category: string) =>
      changeLine(supplierId, invoiceId, lineId, { category }),
    [changeLine]
  );

  const setIncludeTaxes = useCallback(
    (supplierId: string, included: boolean) =>
      showThenSend(
        (current) => withSupplierChange(current, supplierId, { include_taxes: included }),
        (id) => updateAdviseCurrentSupplier(id, supplierId, { include_taxes: included })
      ),
    [showThenSend]
  );

  const saveContract = useCallback(
    (supplierId: string, change: AdviseSupplierContract) =>
      showThenSend(
        (current) => withContractChange(current, supplierId, change),
        (id, changed) => {
          if (!findSupplier(changed, supplierId)) return Promise.resolve(NOT_SENT);
          return updateAdviseCurrentSupplier(id, supplierId, { contract: change });
        }
      ),
    [showThenSend]
  );

  return useMemo(
    () => ({ setLineInScope, setLineCategory, setIncludeTaxes, saveContract }),
    [setLineInScope, setLineCategory, setIncludeTaxes, saveContract]
  );
};
